<template>
  <div>
    <b-dropdown-divider></b-dropdown-divider>
    <b-dropdown-item @click="showDownloadModal" id="popover-target-2">
      <font-awesome-icon icon="download" class="icon mr-2"/>
      {{ $t('Download Bulk Edit Template') }}
    </b-dropdown-item>
    <b-popover target="popover-target-2" triggers="hover" placement="right">
      <template v-slot:title>{{ $t('Xlsx Template') }}</template>
      {{
        $t('For upload feature to function properly, please make sure no structural alterations are made to the downloaded template.')
      }}
    </b-popover>
    <b-dropdown-item @click="$refs.bulkEditInput.click()">
      <font-awesome-icon icon="upload" class="icon mr-2"/>
      {{ $t('Upload Bulk Edit') }}
    </b-dropdown-item>
    <input :multiple="false" @change="onUploadChange"
           accept=".xlsx" type="file" class="d-none"
           ref="bulkEditInput"/>

    <artisio-modal :visible="showModal" :title="$t('Download Bulk Edit Template')"
                   :ok-title="$t('Download')" :disable-submit-btn="!isDownloadValid"
                   @ok="onDownload" @hidden="showModal = false"
                   :content-in-form="false" size="lg">
      <b-form-group :label="$t('Template Type')">
        <b-form-radio-group v-model="templateType" stacked>
          <b-form-radio value="empty">{{ $t('Empty Template') }}</b-form-radio>
          <b-form-radio value="pre_populated">{{ $t('Pre-populated Template') }}</b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <div v-if="templateType === 'pre_populated'">
        <div class="row">
          <div class="col-md-6">
            <receipt-select-input-widget
              :model="formModel"
              attribute="receipt_uuids"
              :label="$t('Select Receipts')"
              :multiple="true"
              :disabled="disableReceiptSelect"
              :options="{minimumInputLength: 1, multiple: true}"
              @select="onReceiptChange"
              @unselect="onReceiptChange"
            />
          </div>
          <div class="col-md-6">
            <sale-select-input-widget
              :model="formModel"
              attribute="auction_uuids"
              :label="$t('Select Sales')"
              :multiple="true"
              :disabled="disableSaleSelect"
              :options="{minimumInputLength: 1, multiple: true}"
              @select="onSaleChange"
              @unselect="onSaleChange"
            />
          </div>
        </div>
        <b-alert v-if="validationError" variant="danger" show class="mt-2">
          {{ validationError }}
          <ul v-if="validationBreakdown.length" class="mb-0 mt-1">
            <li v-for="(entry, idx) in validationBreakdown" :key="idx">{{ entry }}</li>
          </ul>
        </b-alert>
        <b-alert v-else-if="(formModel.receipt_uuids && formModel.receipt_uuids.length) || (formModel.auction_uuids && formModel.auction_uuids.length) > 0" variant="info" show class="mt-2">
          {{ itemCount }} {{ $t('items selected') }}
        </b-alert>
      </div>
    </artisio-modal>
  </div>
</template>

<script>
  import InventoryService from "@/modules/inventory/InventoryService";
  import ReceiptSelectInputWidget from "@/core/widgets/ReceiptSelectInputWidget";
  import SaleSelectInputWidget from "@/core/widgets/SaleSelectInputWidget";
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import BulkEditDownloadModel from "@/modules/inventory/BulkEditDownload.model";
  import {createNamespacedHelpers} from "vuex";
  import {WS_BULK_EDIT_UPLOAD_PROGRESS} from "@/modules/constants";

  const {mapActions: mapInventoryActions} = createNamespacedHelpers('inventory');
  const {mapActions: mapGlobalActions} = createNamespacedHelpers('global');

  export default {
    name: "InventoryBulkEditUpload",
    components: {ReceiptSelectInputWidget, SaleSelectInputWidget, ArtisioModal},
    data() {
      return {
        showModal: false,
        templateType: 'empty',
        formModel: new BulkEditDownloadModel(),
        validationError: null,
        validationReceipts: [],
        validationSales: [],
        itemCount: 0,
        bulkEditProgress: null
      }
    },
    computed: {
      disableReceiptSelect() {
        return !!(this.formModel.auction_uuids && this.formModel.auction_uuids.length > 0);
      },
      disableSaleSelect() {
        return !!(this.formModel.receipt_uuids && this.formModel.receipt_uuids.length > 0);
      },
      validationBreakdown() {
        return this.validationReceipts.length ? this.validationReceipts : this.validationSales;
      },
      isDownloadValid() {
        if (this.templateType === 'empty') return true;
        const hasReceipts = this.formModel.receipt_uuids && this.formModel.receipt_uuids.length > 0;
        const hasSales = this.formModel.auction_uuids && this.formModel.auction_uuids.length > 0;
        return (hasReceipts || hasSales) && !this.validationError;
      }
    },
    watch: {
      templateType() {
        if (this.templateType === 'empty') {
          this.formModel.receipt_uuids = [];
          this.formModel.auction_uuids = [];
        }
      }
    },
    methods: {
      ...mapInventoryActions(['getTableData']),
      ...mapGlobalActions(['addProgress', 'removeProgress', 'updateProgressValue']),
      showDownloadModal() {
        this.templateType = 'empty';
        this.formModel = new BulkEditDownloadModel();
        this.resetValidation();
        this.showModal = true;
      },
      resetValidation() {
        this.validationError = null;
        this.validationReceipts = [];
        this.validationSales = [];
        this.itemCount = 0;
      },
      getValidationParams() {
        return {
          receipt_uuids: this.formModel.receipt_uuids || [],
          auction_uuids: this.formModel.auction_uuids || []
        };
      },
      async onReceiptChange() {
        this.formModel.auction_uuids = [];
        await this.validateSelection();
      },
      async onSaleChange() {
        this.formModel.receipt_uuids = [];
        await this.validateSelection();
      },
      async validateSelection() {
        this.resetValidation();
        await this.$nextTick();
        const {receipt_uuids, auction_uuids} = this.getValidationParams();
        if (!receipt_uuids.length && !auction_uuids.length) return;

        let response = await InventoryService.validateBulkEditTemplate(this.getValidationParams());
        if (response.success) {
          this.itemCount = response.body.item_count;
        } else {
          if (response.body.non_field_errors) {
            this.validationError = response.body.non_field_errors[0];
          }
          if (response.body.receipts && response.body.receipts.length) {
            this.validationReceipts = typeof response.body.receipts[0] === 'string'
              ? response.body.receipts
              : response.body.receipts.map(r => `${r.receipt_no}: ${r.count} items`);
          }
          console.log(response.body.sales)
          if (response.body.sales && response.body.sales.length) {
            this.validationSales = typeof response.body.sales[0] === 'string'
              ? response.body.sales
              : response.body.sales.map(s => `${s.sale_no}: ${s.count} items`);
          }
        }
      },
      async onDownload() {
        let response = await InventoryService.downloadBulkEditTemplate({
          template_type: this.templateType,
          ...(this.templateType === 'pre_populated' ? this.getValidationParams() : {
            receipt_uuids: [],
            auction_uuids: []
          })
        });
        if (response.success) {
          this.$toast('info', this.$i18n.t('Preparing template... You will be notified when ready.'));
        }
        this.showModal = false;
      },
      async onUploadChange($event) {
        const files = $event.target.files;
        if (files.length === 0) return;

        const formData = new FormData();
        formData.append('file', files[0]);
        let response = await InventoryService.uploadBulkEditTemplate(formData);

        if (response.success) {
          this.$toast('info', this.$i18n.t('Processing bulk edit... You will be notified when complete.'));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Bulk Edit Validation Errors'));
          } else if (response.body.errors) {
            const errorCount = response.body.error_count || response.body.errors.length;
            const headerEl = this.$createElement('p', {class: 'font-weight-bold mb-2'},
              `${errorCount} ${this.$i18n.t('errors found. Please fix the following issues and re-upload.')}`
            );
            const listItems = response.body.errors.map(err => this.$createElement('li', {}, err));
            const listEl = this.$createElement('ul', {class: 'mb-0'}, listItems);
            this.$errorDialog(
              this.$createElement('div', {style: 'max-height: 400px; overflow-y: auto;'}, [headerEl, listEl]),
              this.$i18n.t('Bulk Edit Validation Errors')
            );
          }
        }
        this.$refs.bulkEditInput.value = null;
      },
      async addBulkEditProgressIfNotExist() {
        if (!this.bulkEditProgress) {
          this.bulkEditProgress = await this.addProgress(
            this.$i18n.t("Processing bulk edit")
          );
        }
        return true;
      },
      removeBulkEditProgressIfExist() {
        if (this.bulkEditProgress) {
          this.removeProgress(this.bulkEditProgress.uuid);
          this.bulkEditProgress = null;
        }
      },
      async onBulkEditUploadProgress(data) {
        await this.addBulkEditProgressIfNotExist();
        if (data.total > 0) {
          this.updateProgressValue({
            progressValue: parseInt(Math.round(data.processed / data.total * 100)),
            uuid: this.bulkEditProgress.uuid
          });
        }
        if (data.is_finished) {
          if (data.errors) {
            if (data.errors.non_field_errors) {
              this.$errorDialog(data.errors.non_field_errors[0], this.$i18n.t('Bulk Edit Validation Errors'));
            } else if (data.errors.errors) {
              const errorCount = data.errors.error_count || data.errors.errors.length;
              const headerText = data.error_log_url
                ? `${errorCount} ${this.$i18n.t('errors found. Showing first 10. Download the full error log below.')}`
                : `${errorCount} ${this.$i18n.t('errors found. Please fix the following issues and re-upload.')}`;
              const headerEl = this.$createElement('p', {class: 'font-weight-bold mb-2'}, headerText);
              const listItems = data.errors.errors.map(err => this.$createElement('li', {}, err));
              const listEl = this.$createElement('ul', {class: 'mb-0'}, listItems);
              const elements = [headerEl, listEl];

              if (data.error_log_url) {
                const downloadBtn = this.$createElement('b-button', {
                  props: {variant: 'outline-primary', size: 'sm', href: data.error_log_url},
                  attrs: {target: '_blank'},
                  class: 'mt-2'
                }, this.$i18n.t('Download Full Error Log'));
                elements.push(downloadBtn);
              }

              this.$errorDialog(
                this.$createElement('div', {style: 'max-height: 400px; overflow-y: auto;'}, elements),
                this.$i18n.t('Bulk Edit Validation Errors')
              );
            }
          } else if (data.message) {
            this.$toast('success', data.message);
          }
          this.removeBulkEditProgressIfExist();
          this.getTableData();
        }
      }
    },
    mounted() {
      this.$ws.listen(`inventory.${WS_BULK_EDIT_UPLOAD_PROGRESS}`, this.onBulkEditUploadProgress);
    },
    beforeDestroy() {
      this.removeBulkEditProgressIfExist();
      this.$ws.remove(`inventory.${WS_BULK_EDIT_UPLOAD_PROGRESS}`);
    }
  }
</script>

<style scoped>
</style>
